import { HttpResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { MatSort } from '@angular/material/sort';
import { MatTable } from '@angular/material/table';
import { I18nService } from '@app/i18n/services';
import { FeedbackService, FeedbackStyle, FeedbackTitle } from '@services/feedback';
import { ProgressIndicatorService } from '@services/progress';
import { LegalsService } from '@services/legals/legals.service';

import { ISortedAppTerm, TermState, TermType } from '../../../../../../services/legals';
import { AppKeyAssociateLegal } from './appkey-associate-legal.models';

@Component({
	selector: 'app-appkey-associate-legal',
	templateUrl: './appkey-associate-legal.dialog.html',
	styleUrls: ['./appkey-associate-legal.dialog.scss']
})
export class AppKeyAssociateLegalDialogComponent implements OnInit, AfterViewInit {
	@ViewChild('legalsSelect') legalsSelect: MatSelect;
	@ViewChild(MatSort, { static: false }) sort: MatSort;
	@ViewChild(MatTable) table: MatTable<any>;

	public formInputs = this.formBuilder.group({
		legals: ['']
	});

	public tableColumns: string[] = ['displayName', 'alias', 'version', 'action'];
	public selectedLegals: ISortedAppTerm[] = [];
	public legals: ISortedAppTerm[] = [];

	constructor(
		private legalsService: LegalsService,
		private i18nService: I18nService,
		private progressIndicatorService: ProgressIndicatorService,
		private feedbackService: FeedbackService,
		@Inject(MAT_DIALOG_DATA) public dialogData: AppKeyAssociateLegal,
		private dialogRef: MatDialogRef<AppKeyAssociateLegalDialogComponent>,
		private formBuilder: FormBuilder
	) {}

	public ngOnInit(): void {
		this.legals = [...this.dialogData.terms].sort((a, b) => {
			return a.displayName.localeCompare(b.displayName);
		});

		this.formInputs.controls.legals.valueChanges.subscribe((value) => {
			if (value) {
				this.selectedLegals = [...this.selectedLegals, value].sort((a, b) => {
					return a.displayName.localeCompare(b.displayName);
				});

				this.legals = this.legals.filter((legal) => legal.termsId !== value.termsId);
				this.formInputs.controls.legals.setValue('', { emitEvent: false });
				this.sortList();
			}
		});
	}

	public ngAfterViewInit(): void {
		this.sort.sortChange.subscribe((value: { active: string; direction: string }) => {
			this.sortList(value.active, value.direction);
		});
	}

	public selectAll(): void {
		this.selectedLegals = [...this.selectedLegals, ...this.legals].sort((a, b) => {
			return a.displayName.localeCompare(b.displayName);
		});
		this.legals = [];
		this.legalsSelect.close();
		this.sortList();
	}

	public removeAllLegals(): void {
		this.selectedLegals = [];
		this.filterLegals();
	}

	public onFormSubmit(): void {
		this.feedbackService.confirm({
			title: FeedbackTitle.attention,
			message: this.i18nService.get(
				'This will associate the selected legal documents with the app key. Are you sure you want to continue?',
				'app.prompt.legal.associate-to-app-key'
			),
			style: FeedbackStyle.error,
			callback: (confirmed: boolean) => {
				if (confirmed) {
					this.associateToTerms();
				}
			}
		});
	}

	public removeLegal(legal: ISortedAppTerm): void {
		this.selectedLegals = this.selectedLegals.filter((selectedLegal) => selectedLegal.termsId !== legal.termsId);
		this.filterLegals();
	}

	public viewTerm(term: ISortedAppTerm): void {
		if (term.documents[term.activeVersion]?.url) {
			this.legalsService.serveExternalDocument(term.documents[term.activeVersion].url);
		} else {
			this.progressIndicatorService.spinner(true);

			this.legalsService.getTermData(term.termsId, term.activeVersion, TermType.app).subscribe(
				(res: HttpResponse<string>) => {
					this.legalsService.serveDocument(
						this.legalsService.getAppTermDocument(term, term.activeVersion),
						res,
						false,
						TermType.app,
						TermState.live,
						null
					);
					this.progressIndicatorService.spinner(false);
				},
				(error: any) => {
					this.feedbackService.apiError(error);
					this.progressIndicatorService.spinner(false);
				}
			);
		}
	}

	private sortList(active = this.sort.active, direction = this.sort.direction.toString()): void {
		this.selectedLegals.sort((a, b) => {
			const aValue = a[active] ? a[active] : '';
			const bValue = b[active] ? b[active] : '';

			return direction === 'asc'
				? bValue?.localeCompare(aValue)
				: direction === 'desc'
				? aValue?.localeCompare(bValue)
				: a.displayName.localeCompare(b.displayName);
		});

		this.table.renderRows();
	}

	private filterLegals(): void {
		this.legals = this.dialogData.terms.filter((value) => {
			return this.selectedLegals.findIndex((legal) => legal.termsId === value.termsId) === -1;
		});
	}

	private associateToTerms(): void {
		this.progressIndicatorService.spinner(true);

		this.legalsService
			.associateAppKeyToTerms(
				this.dialogData.appKey.clientId,
				this.selectedLegals.map((legal) => legal.termsId),
				this.selectedLegals
			)
			.subscribe(
				(res: ISortedAppTerm[]) => {
					this.dialogData.appKey.requiredTerms = res.map((term) => {
						return {
							companyId: term.companyId,
							termsId: term.termsId,
							entityType: term.entityType,
							displayName: term.displayName,
							activeVersion: term.activeVersion,
							latestVersion: term.latestVersion
						};
					});

					this.dialogRef.close();
					this.progressIndicatorService.spinner(false);
					this.dialogData.callback(res);
				},
				(error: any) => {
					this.progressIndicatorService.spinner(false);
					this.feedbackService.apiError(error);
				}
			);
	}
}
